// packages/ui/src/components/chat/ChatPresenceChip.tsx
import type { ReactNode } from 'react';
import type { TFunc } from '@tourtask/types/i18n';
import { ChatPresenceDot } from './ChatPresenceDot';

interface ChatPresenceChipProps {
  /** A human agent is online right now (the green-dot truth, docs/chat-architecture.md §3). */
  humanOnline: boolean;
  /**
   * Whether an AI bot covers this desk — it always answers, so we never show
   * "offline" when it's on. `null` means "not known yet" (e.g. the embed bootstrap
   * hasn't resolved): treated like AI-might-be-on, so we don't assert "offline"
   * without data.
   */
  aiEnabled: boolean | null;
  t: TFunc;
  /** Label for the green "a human is here" chip. */
  onlineLabel: string;
  /** Label for the muted "nobody answers" chip. */
  offlineLabel: string;
}

/**
 * The shared support / website-chat presence-chip policy, used by both the
 * backoffice support widget and the embed website widget so the rule can't drift
 * between the two surfaces:
 *
 * - human online            → green "online" chip,
 * - nobody answers           → muted "offline" chip (no human AND AI provably off),
 * - the AI bot covers it      → **nothing** — it replies instantly, so "offline"
 *   would mislead and "online" would wrongly imply a human is on the line.
 *
 * The third case also covers `aiEnabled === null` (availability unknown): we render
 * no chip rather than assert "offline" on missing data.
 */
export function ChatPresenceChip({
  humanOnline,
  aiEnabled,
  t,
  onlineLabel,
  offlineLabel,
}: ChatPresenceChipProps): ReactNode {
  if (humanOnline) return <ChatPresenceDot online t={t} chip label={onlineLabel} />;
  // Show "offline" only when we KNOW there is no AI bot to fall back on.
  if (aiEnabled === false) return <ChatPresenceDot online={false} t={t} chip label={offlineLabel} />;
  return null;
}
